import { IAccount, ICustomerDetail } from "@interfaces";
import { breakpoint } from "@utilities";
import { isPlaceholderPhone } from "../signupOfferConfig";
import { resolveTravellerDetails } from "../travellerDetails";
import _ from "lodash";
import styled, { keyframes } from "styled-components";

/** Lookup miss from getCountryName — filtered out rather than shown. */
const COUNTRY_NOT_FOUND = "Not Found";

export interface Suggestion {
    first_name: string | null;
    last_name: string | null;
    phone_number: number;
    country_code: string;
    email: string;
    id: string;
}

interface TravelerSuggestionsBottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    suggestions: ICustomerDetail[];
    onRemove: (travelerId: number) => void;
    countriesData: any[];
    /** Live source for the account holder's own row — see resolveTravellerDetails. */
    account?: IAccount | null;
}

export const TravellerRemoveBox = ({
    isOpen,
    onClose,
    suggestions,
    countriesData,
    onRemove,
    account,
}: TravelerSuggestionsBottomSheetProps) => {
    if (!isOpen) return null;

    const getCountryName = (targetCode: string | undefined) =>
        _.get(_.find(countriesData, { countryCode: targetCode }), "name", COUNTRY_NOT_FOUND);

    return (
        <>
            <Overlay onClick={onClose} />
            <BottomSheet onClick={(e) => e.stopPropagation()}>
                <div className="details-container">
                    <h2>Selected Travellers</h2>
                    {_.map(suggestions, (user) => {
                        const { firstName, lastName, phoneNumber, countryCode, email } =
                            resolveTravellerDetails(user, account);

                        // Same rule as the suggestions list: a stand-in phone and
                        // an unresolvable country code are noise, not details.
                        const countryName = countryCode ? getCountryName(countryCode) : undefined;
                        const subtitle = [
                            email,
                            isPlaceholderPhone(phoneNumber) ? undefined : phoneNumber,
                            countryName === COUNTRY_NOT_FOUND ? undefined : countryName,
                        ]
                            .filter(Boolean)
                            .join(", ");

                        return (
                            <div className="details" key={user.id}>
                                <NameContainer>
                                    <div>
                                        <h1>
                                            {firstName} {lastName}{" "}
                                            {user.is_primary && "(you)"}
                                        </h1>
                                        {subtitle && <p>{subtitle}</p>}
                                    </div>
                                    <button type="button" onClick={() => onRemove(Number(user.id))}>
                                        <img src="/images/deleteSvgButton.svg" alt="delete" />
                                    </button>
                                </NameContainer>
                            </div>
                        );
                    })}
                </div>
            </BottomSheet>
        </>
    );
};

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const BottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;
        .details-container{
            display: flex;
            flex-direction: column;
            /* Use margin instead of gap for Safari mobile compatibility */
            .details {
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
            h2 {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                div{
                    display : flex;
                    flex-direction : column;
                    /* Use margin instead of gap for Safari mobile compatibility */
                    h1 {
                        font-size : 24px;
                        font-weight : 600;
                        margin-bottom: 6px;
                    }
                    p {
                        font-size : 12px;
                        color : #6E7375;
                        margin-bottom: 0;
                    }
                }
            }
            
        }
    `,
    )}

    /* Force show on mobile devices */
    @media (max-width: 768px) {
        display: block !important;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;
        /* Safari mobile specific properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);

        .details-container {
            .details {
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
            h2 {
                margin-bottom: 20px;
            }
            .details div h1 {
                margin-bottom: 6px;
            }
        }
    }

    /* Ensure visibility on very small screens */
    @media (max-width: 375px) {
        display: block !important;
        padding: 20px 12px 24px 12px;
    }

    /* Safari-specific mobile support */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 768px) {
            display: block !important;
        }
    }

    /* Additional mobile breakpoints */
    @media (max-width: 480px) {
        display: block !important;
    }

    /* Force mobile layout on small screens */
    @media (max-width: 767px) {
        display: block !important;
        width: 100vw;
        max-width: 100vw;
        /* Additional Safari mobile properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
        /* Ensure proper positioning */
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
    }

    /* Desktop view styles */
    ${breakpoint.breakTablet`
        display: block;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 700px;
        max-width: 90vw;
        z-index: 15;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        padding: 38px 24px 0px 24px;
        animation: none;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        
        .details-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
            }
            .details {
                display: flex;
                align-items: center;
                gap: 16px;
                div {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    h1 {
                        font-size: 24px;
                        font-weight: 600;
                    }
                    p {
                        font-size: 12px;
                        color: #6e7375;
                    }
                }
            }
        }
    `}

    /* Large desktop view styles */
    ${breakpoint.breakLarge`
        width: 800px;
        padding: 48px 32px 0px 32px;
        
        .details-container {
            h2 {
                font-size: 24px;
            }
            .details {
                div {
                    h1 {
                        font-size: 28px;
                    }
                    p {
                        font-size: 14px;
                    }
                }
            }
        }
    `}
`;

const NameContainer = styled.div`
    display: flex;
    flex-direction: row !important;
    justify-content: space-between;
    width: 100%;

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        button {
            /* Ensure touch targets are properly sized */
            min-height: 44px;
            min-width: 44px;
            /* Safari mobile touch improvements */
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            user-select: none;
        }
    }

    /* Desktop button improvements */
    ${breakpoint.breakTablet`
        button {
            transition: all 0.2s ease;
            &:hover {
                transform: scale(1.05);
                opacity: 0.8;
            }
        }
    `}
`;

const Overlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;

    /* Force show on mobile devices */
    @media (max-width: 768px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    /* Safari-specific mobile support */
    @supports (-webkit-touch-callout: none) {
        @media (max-width: 768px) {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
        }
    }

    /* Additional mobile breakpoints */
    @media (max-width: 480px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    @media (max-width: 375px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    /* Force mobile layout on small screens */
    @media (max-width: 767px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        /* Safari mobile specific properties */
        -webkit-overflow-scrolling: touch;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
    }

    /* Desktop view - ensure overlay is visible and clickable */
    ${breakpoint.breakTablet`
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 10;
    `}
`;
